/**
 * Centralized configuration module for Firebase and application settings.
 * This module loads configuration from environment variables and validates
 * them before the application initializes.
 *
 * Validates: Requirements 1.1, 1.2, 1.3
 */

export interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId?: string
}

export interface EmulatorConfig {
  enabled: boolean
  firestore: { host: string; port: number }
  auth: { host: string; port: number }
  functions: { host: string; port: number }
}

export interface EnvironmentConfig {
  environment: 'development' | 'staging' | 'production'
  firebase: FirebaseConfig
  emulators: EmulatorConfig
}

/**
 * Configuration validation error with descriptive message
 */
export class ConfigurationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ConfigurationError'
  }
}

/**
 * Validates that all required Firebase configuration fields are present
 * and non-empty.
 *
 * @param config - The configuration object to validate
 * @throws {ConfigurationError} If any required field is missing or empty
 */
export function validateConfig(config: Partial<EnvironmentConfig>): void {
  const requiredFields: Array<keyof EnvironmentConfig> = [
    'environment',
    'firebase',
    'emulators'
  ]

  // Check top-level required fields
  for (const field of requiredFields) {
    if (!config[field]) {
      throw new ConfigurationError(
        `Missing required configuration field: ${field}`
      )
    }
  }

  // Validate Firebase configuration fields
  const firebaseConfig = config.firebase as Partial<FirebaseConfig>
  const requiredFirebaseFields: Array<keyof FirebaseConfig> = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId'
  ]

  for (const field of requiredFirebaseFields) {
    if (!firebaseConfig[field] || firebaseConfig[field] === '') {
      throw new ConfigurationError(
        `Missing required Firebase configuration field: firebase.${field}`
      )
    }
  }

  // Validate emulator configuration structure
  const emulatorConfig = config.emulators as Partial<EmulatorConfig>
  if (typeof emulatorConfig.enabled !== 'boolean') {
    throw new ConfigurationError(
      'Invalid emulator configuration: enabled must be a boolean'
    )
  }

  if (emulatorConfig.enabled) {
    const requiredEmulatorServices = ['firestore', 'auth', 'functions']
    for (const service of requiredEmulatorServices) {
      const serviceConfig = emulatorConfig[service as keyof EmulatorConfig]
      if (!serviceConfig || typeof serviceConfig !== 'object') {
        throw new ConfigurationError(
          `Missing required emulator configuration: emulators.${service}`
        )
      }
      if (
        !('host' in serviceConfig) ||
        !('port' in serviceConfig) ||
        typeof serviceConfig.port !== 'number'
      ) {
        throw new ConfigurationError(
          `Invalid emulator configuration for ${service}: must have host (string) and port (number)`
        )
      }
    }
  }
}

/**
 * Determines the current environment based on REACT_APP_ENVIRONMENT, NODE_ENV, and hostname.
 *
 * Priority order:
 * 1. REACT_APP_ENVIRONMENT (explicit environment specification for build-time detection)
 * 2. NODE_ENV and hostname (fallback for backward compatibility)
 *
 * REACT_APP_ENVIRONMENT should be set in .env.staging and .env.production to ensure
 * correct environment detection during the build process.
 *
 * Defaults to 'development' if not specified.
 */
function getEnvironment(): 'development' | 'staging' | 'production' {
  // Check REACT_APP_ENVIRONMENT first for explicit environment specification
  // This works at build time and allows distinguishing staging from production
  const reactAppEnv = process.env.REACT_APP_ENVIRONMENT

  if (reactAppEnv === 'staging') {
    return 'staging'
  }

  if (reactAppEnv === 'production') {
    return 'production'
  }

  if (reactAppEnv === 'development') {
    return 'development'
  }

  // Fallback to existing NODE_ENV logic for backward compatibility
  const nodeEnv = process.env.NODE_ENV

  if (nodeEnv === 'production') {
    // In production, check hostname to determine if staging or production
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname
      if (hostname.includes('staging')) {
        return 'staging'
      }
    }
    return 'production'
  }

  return 'development'
}

/**
 * Loads Firebase configuration from environment variables or hardcoded configs.
 * In production, environment variables should be used.
 * In development, falls back to hardcoded configs for convenience.
 */
function getFirebaseConfig(): FirebaseConfig {
  const environment = getEnvironment()

  // Try to load from environment variables first
  if (
    process.env.REACT_APP_FIREBASE_API_KEY &&
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN &&
    process.env.REACT_APP_FIREBASE_PROJECT_ID &&
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET &&
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID &&
    process.env.REACT_APP_FIREBASE_APP_ID
  ) {
    return {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
    }
  }

  // Fallback to hardcoded configs for development
  // This maintains backward compatibility with existing setup
  if (environment === 'development') {
    // Determine which project to use based on hostname
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname

      // Check if we're on the hotdesk domain
      if (hostname.includes('hotdesk')) {
        return {
          apiKey: 'AIzaSyDN_Aaz-VfNlb_R-JD1SXJxg2ljOLgVpf0',
          authDomain: 'meetforwork.app',
          projectId: 'hotdesk-34d9a',
          storageBucket: 'hotdesk-34d9a.appspot.com',
          messagingSenderId: '865774902161',
          appId: '1:865774902161:web:d4c25bf216e3478dc75830',
          measurementId: 'G-RLVLDZFT8J'
        }
      }
    }

    // Default to ouroffice for localhost and other domains
    return {
      apiKey: 'AIzaSyBVTGX2mmE7PKxkfZOuNIAiSAsISvq1UpQ',
      authDomain: 'ouroffice-ba025.firebaseapp.com',
      projectId: 'ouroffice-ba025',
      storageBucket: 'ouroffice-ba025.firebasestorage.app',
      messagingSenderId: '710992768109',
      appId: '1:710992768109:web:5d178c69dc144fcc04a364',
      measurementId: 'G-WG8F73XJQK'
    }
  }

  // For staging/production without env vars, throw error
  throw new ConfigurationError(
    'Firebase configuration must be provided via environment variables in staging/production environments'
  )
}

/**
 * Loads emulator configuration based on the current environment.
 * Emulators are enabled in development and test environments only.
 */
function getEmulatorConfig(): EmulatorConfig {
  const environment = getEnvironment()
  const isTest = process.env.NODE_ENV === 'test'

  // Force emulators to be enabled in development
  // This ensures we never accidentally connect to production Firestore
  const shouldEnableEmulators =
    environment === 'development' ||
    isTest ||
    process.env.REACT_APP_USE_EMULATORS === 'true'

  return {
    enabled: shouldEnableEmulators,
    firestore: {
      host: process.env.REACT_APP_FIRESTORE_EMULATOR_HOST || 'localhost',
      port: parseInt(
        process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || '8080',
        10
      )
    },
    auth: {
      host: process.env.REACT_APP_AUTH_EMULATOR_HOST || 'localhost',
      port: parseInt(process.env.REACT_APP_AUTH_EMULATOR_PORT || '9099', 10)
    },
    functions: {
      host: process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST || 'localhost',
      port: parseInt(
        process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001',
        10
      )
    }
  }
}

/**
 * Loads and validates the complete application configuration.
 * This function should be called once during application initialization.
 *
 * @returns {EnvironmentConfig} The validated configuration object
 * @throws {ConfigurationError} If configuration is invalid or incomplete
 */
export function getConfig(): EnvironmentConfig {
  const config: EnvironmentConfig = {
    environment: getEnvironment(),
    firebase: getFirebaseConfig(),
    emulators: getEmulatorConfig()
  }

  // Validate the configuration before returning
  validateConfig(config)

  return config
}

/**
 * Checks if pricing/subscription features are enabled.
 * Defaults to false if not specified.
 *
 * @returns {boolean} True if pricing features should be shown
 */
export function isPricingEnabled(): boolean {
  return process.env.REACT_APP_ENABLE_PRICING === 'true'
}

/**
 * Gets the application title for display in the top bar.
 * Defaults to 'Our Office portal' if not specified.
 *
 * @returns {string} The application title
 */
export function getAppTitle(): string {
  return process.env.REACT_APP_TITLE || 'Our Office portal'
}
